const phpCheatsheet = {
  title: "PHP",
  level: "All Levels",
  description: "Modern PHP features, patterns, and best practices",
  language: "php",
  sections: [
    {
      title: "Getting Started",
      icon: "🐘",
      content: [
        {
          title: "Basic Syntax",
          code: `<?php
// PHP tags
<?php // Start PHP code
?> // End PHP code

// Variables
$name = "John";
$age = 30;

// Constants
define('MAX_VALUE', 100);
const MIN_VALUE = 0;

// Comments
// Single line comment
# Alternative single line comment
/* Multi-line
   comment */

// Output
echo "Hello World"; // Print string
print_r($array); // Print array/object
var_dump($var); // Debug output
`,
          explanation:
            "Basic PHP syntax including tags, variables, constants, and output methods.",
        },
        {
          title: "Data Types",
          code: `<?php
// Scalar Types
$string = "Hello";
$integer = 42;
$float = 3.14;
$boolean = true;

// Compound Types
$array = ["apple", "banana"];
$object = new stdClass();

// Special Types
$null = null;
$resource = fopen("file.txt", "r");

// Type Declarations (PHP 7+)
declare(strict_types=1);

function add(int $a, int $b): int {
    return $a + $b;
}

// Type Checking
var_dump(is_string($string));
var_dump(is_int($integer));
var_dump(is_array($array));`,
          explanation: "PHP data types and type declarations.",
        },
      ],
    },
    {
      title: "Control Structures",
      icon: "🔄",
      content: [
        {
          title: "Conditionals",
          code: `<?php
// If Statement
if ($age >= 18) {
    echo "Adult";
} elseif ($age >= 13) {
    echo "Teenager";
} else {
    echo "Child";
}

// Switch Statement
switch ($fruit) {
    case "apple":
        echo "Selected apple";
        break;
    case "banana":
        echo "Selected banana";
        break;
    default:
        echo "Unknown fruit";
}

// Match Expression (PHP 8+)
$result = match ($status) {
    200, 300 => "Success",
    400, 500 => "Error",
    default => "Unknown"
};

// Ternary Operator
$status = ($age >= 18) ? "Adult" : "Minor";

// Null Coalescing
$name = $_GET['name'] ?? 'Anonymous';`,
          explanation: "Control structures and conditional statements in PHP.",
        },
        {
          title: "Loops",
          code: `<?php
// For Loop
for ($i = 0; $i < 5; $i++) {
    echo $i;
}

// While Loop
while ($active) {
    process_data();
}

// Do-While Loop
do {
    echo "Run once at least";
} while ($condition);

// Foreach Loop
foreach ($fruits as $fruit) {
    echo $fruit;
}

// Foreach with Key
foreach ($users as $id => $user) {
    echo "$id: $user";
}

// Break and Continue
foreach ($items as $item) {
    if ($item->isEmpty()) continue;
    if ($item->isLast()) break;
    process($item);
}`,
          explanation: "Different types of loops in PHP.",
        },
      ],
    },
    {
      title: "Functions",
      icon: "⚙️",
      content: [
        {
          title: "Function Basics",
          code: `<?php
// Basic Function
function greet($name) {
    return "Hello, $name!";
}

// Default Parameters
function connect($host = 'localhost', $port = 3306) {
    // Connection logic
}

// Type Declarations
function add(int $a, int $b): int {
    return $a + $b;
}

// Variable Arguments
function sum(...$numbers) {
    return array_sum($numbers);
}

// Arrow Functions (PHP 7.4+)
$double = fn($x) => $x * 2;

// Closures
$multiplier = function($x) use ($factor) {
    return $x * $factor;
};`,
          explanation: "Function definitions and usage in PHP.",
        },
        {
          title: "Built-in Functions",
          code: `<?php
// String Functions
strlen($str);        // String length
substr($str, 0, 5);  // Get substring
str_replace($search, $replace, $subject);

// Array Functions
array_push($arr, $value);
array_pop($arr);
array_map(fn($x) => $x * 2, $arr);
array_filter($arr, fn($x) => $x > 0);

// Math Functions
abs(-5);      // Absolute value
round(3.14);  // Round number
rand(1, 100); // Random number

// Date/Time Functions
date('Y-m-d');
time();
strtotime('+1 week');`,
          explanation: "Common built-in PHP functions.",
        },
      ],
    },
    {
      title: "Arrays",
      icon: "📚",
      content: [
        {
          title: "Array Operations",
          code: `<?php
// Creating Arrays
$numbers = [1, 2, 3, 4, 5];
$fruits = array('apple', 'banana', 'orange');

// Associative Arrays
$user = [
    'name' => 'John',
    'age' => 30,
    'email' => 'john@example.com'
];

// Multidimensional Arrays
$matrix = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9]
];

// Array Operations
$merged = array_merge($arr1, $arr2);
$filtered = array_filter($numbers, fn($n) => $n > 2);
$mapped = array_map(fn($n) => $n * 2, $numbers);

// Array Destructuring (PHP 7.1+)
[$a, $b] = $array;
['name' => $name, 'age' => $age] = $user;`,
          explanation: "Working with arrays in PHP.",
        },
        {
          title: "Array Functions",
          code: `<?php
// Checking Arrays
isset($array['key']);     // Key exists and not null
array_key_exists('key', $array); // Key exists
in_array($value, $array); // Value exists

// Manipulating Arrays
sort($array);           // Sort indexed array
asort($array);         // Sort associative array by values
ksort($array);         // Sort associative array by keys
array_reverse($array); // Reverse array

// Array Information
count($array);         // Count elements
array_sum($numbers);   // Sum of elements
array_unique($array);  // Remove duplicates
array_slice($array, 0, 3); // Get portion of array`,
          explanation: "Common array functions in PHP.",
        },
      ],
    },
    {
      title: "Object-Oriented Programming",
      icon: "🎯",
      content: [
        {
          title: "Classes and Objects",
          code: `<?php
// Class Definition
class User {
    // Properties
    private string $name;
    protected int $age;
    public string $email;

    // Constructor
    public function __construct(
        string $name,
        int $age,
        string $email
    ) {
        $this->name = $name;
        $this->age = $age;
        $this->email = $email;
    }

    // Methods
    public function getName(): string {
        return $this->name;
    }

    // Static Method
    public static function create($data): self {
        return new self(
            $data['name'],
            $data['age'],
            $data['email']
        );
    }
}

// Using Classes
$user = new User("John", 30, "john@example.com");
echo $user->getName();`,
          explanation: "Object-oriented programming basics in PHP.",
        },
        {
          title: "Inheritance and Interfaces",
          code: `<?php
// Interface
interface Printable {
    public function print(): string;
}

// Abstract Class
abstract class Person {
    abstract public function getRole(): string;
    
    public function getInfo(): string {
        return "Role: " . $this->getRole();
    }
}

// Class Inheritance
class Employee extends Person implements Printable {
    private $position;

    public function getRole(): string {
        return "Employee";
    }

    public function print(): string {
        return $this->getInfo();
    }
}

// Traits
trait Timestampable {
    private $createdAt;

    public function getCreatedAt() {
        return $this->createdAt;
    }
}

class Post {
    use Timestampable;
}`,
          explanation: "Advanced OOP concepts in PHP.",
        },
      ],
    },
    {
      title: "Error Handling",
      icon: "⚠️",
      content: [
        {
          title: "Exception Handling",
          code: `<?php
// Try-Catch Block
try {
    // Risky code
    throw new Exception("Error occurred");
} catch (Exception $e) {
    echo $e->getMessage();
} finally {
    // Always executed
}

// Custom Exception
class ValidationException extends Exception {
    public function __construct($message) {
        parent::__construct($message);
    }
}

// Multiple Catch Blocks
try {
    validateData($data);
} catch (ValidationException $e) {
    // Handle validation error
} catch (Exception $e) {
    // Handle other errors
}`,
          explanation: "Error and exception handling in PHP.",
        },
      ],
    },
    {
      title: "Modern PHP Features",
      icon: "🚀",
      content: [
        {
          title: "PHP 8+ Features",
          code: `<?php
// Named Arguments
setUser(
    email: "john@example.com",
    name: "John"
);

// Constructor Property Promotion
class Point {
    public function __construct(
        public float $x = 0.0,
        public float $y = 0.0,
    ) {}
}

// Match Expression
$result = match ($status) {
    200 => "OK",
    404 => "Not Found",
    default => "Unknown",
};

// Nullsafe Operator
$country = $session?->user?->getAddress()?->country;

// String Functions
str_contains($haystack, $needle);
str_starts_with($haystack, $needle);
str_ends_with($haystack, $needle);`,
          explanation: "Modern PHP 8+ features and syntax.",
        },
        {
          title: "Type System",
          code: `<?php
// Union Types
function process(int|float $number) {
    return $number * 2;
}

// Nullable Types
function findUser(?string $id): ?User {
    return $id ? User::find($id) : null;
}

// Mixed Type
function accept(mixed $value) {
    // Accepts any type
}

// Return Type Declarations
function getData(): array|false {
    // Returns array or false
}

// Strict Types
declare(strict_types=1);

// Type Aliases (PHP 8.2+)
type UserId = int;
type Status = 'active'|'inactive';`,
          explanation: "PHP's type system and type declarations.",
        },
      ],
    },
    {
      title: "Database Operations",
      icon: "🗄️",
      content: [
        {
          title: "PDO Database",
          code: `<?php
// Connection
try {
    $pdo = new PDO(
        "mysql:host=localhost;dbname=testdb",
        $username,
        $password,
        [PDO::ATTR_ERRMODE => PDO::ERRMODE_EXCEPTION]
    );
} catch (PDOException $e) {
    die("Connection failed: " . $e->getMessage());
}

// Prepared Statements
$stmt = $pdo->prepare("
    SELECT * FROM users 
    WHERE age > :age
");
$stmt->execute(['age' => 18]);
$users = $stmt->fetchAll(PDO::FETCH_ASSOC);

// Insert Data
$stmt = $pdo->prepare("
    INSERT INTO users (name, email) 
    VALUES (:name, :email)
");
$stmt->execute([
    'name' => 'John',
    'email' => 'john@example.com'
]);

// Transaction
$pdo->beginTransaction();
try {
    // Multiple operations
    $pdo->commit();
} catch (Exception $e) {
    $pdo->rollBack();
    throw $e;
}`,
          explanation: "Database operations using PDO in PHP.",
        },
      ],
    },
    {
      title: "Security Best Practices",
      icon: "🔒",
      content: [
        {
          title: "Input Validation",
          code: `<?php
// Sanitize Input
$email = filter_var($_POST['email'], FILTER_SANITIZE_EMAIL);
$age = filter_var($_POST['age'], FILTER_SANITIZE_NUMBER_INT);

// Validate Input
if (!filter_var($email, FILTER_VALIDATE_EMAIL)) {
    throw new Exception("Invalid email");
}

// XSS Prevention
$safeHtml = htmlspecialchars($userInput, ENT_QUOTES, 'UTF-8');

// SQL Injection Prevention
$stmt = $pdo->prepare("SELECT * FROM users WHERE id = ?");
$stmt->execute([$userId]);

// Password Hashing
$hashedPassword = password_hash($password, PASSWORD_DEFAULT);
$verified = password_verify($password, $hashedPassword);

// CSRF Protection
session_start();
$token = bin2hex(random_bytes(32));
$_SESSION['csrf_token'] = $token;`,
          explanation: "Security best practices in PHP applications.",
        },
      ],
    },
  ],
};

export default phpCheatsheet;
